<template>
  <PageLayoutWrapper
    page="courses/_id"
    :props="{...$props, course }"
  >
    <article ref="content">
      <div class="flex items-center">
        <h1 class="pb-2 mb-0 flex-grow">{{ courseDisplayHeading }}</h1>
        <span v-if="enablePrintDocuments && course.id" class="pl-2 link text-underline flex items-center" @click="() => downloadDocument(course.id)">
          <BaseIcon
            v-if="isDownloadingDocument"
            icon="spinner"
            class="spinner inline-block mr-2"
          />
          <h2>Download as PDF</h2>
        </span>
      </div>
      <PageTemplateRenderer
        :content="course"
        type="course"
      />
    </article>
  </PageLayoutWrapper>
</template>

<script>
import _uniq from 'lodash/uniq'
import PageLayoutWrapper from '@/components/PageLayoutWrapper'
import pageLayout from '@/mixins/pageLayoutMixin'
import PageTemplateRenderer from '@/components/PageTemplateRenderer'
import CoursesService from '@/services/courses'
import { generateCourseLabel } from '@/utils/dataLabels'
import useInterpolation from '@/composables/useInterpolation'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useDocumentsDownload from '@/composables/useDocumentsDownload'
import { TITLE_INTERPOLATIONS, PRINT_DOCUMENTS } from '@/constants/featureFlags'
import { expandCustomFieldsIntoRootObject } from '@/utils/generalUtils'
import useMeta from '@/composables/useMeta'


export default {
  components: {
    PageLayoutWrapper,
    PageTemplateRenderer,
  },
  mixins: [pageLayout],
  head() {
    return {
      title: `${this.course.code} Course | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `${this.course.code} Course | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  setup() {
    const { enableTitleInterpolations, enablePrintDocuments } = useFeatureFlags(
      {
        enableTitleInterpolations: TITLE_INTERPOLATIONS,
        enablePrintDocuments: PRINT_DOCUMENTS,
      }
    )
    const { getInterpolatedFieldValue } = useInterpolation()
    const { downloadDocument, isDownloadingDocument } = useDocumentsDownload()
    const { catalogWideMetaTitle } = useMeta()
    return {
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintDocuments,
      downloadDocument,
      isDownloadingDocument,
      catalogWideMetaTitle,
    }
  },
  computed: {
    courseDisplayHeading() {
      return generateCourseLabel(this.course, undefined, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
    },
  },
  async asyncData({ params, query, $axios, $bugsnag, redirect, store }) {
    const coursePageTemplate = store.state.settings.coursePageTemplate
    const courseQuestions = Object.values(coursePageTemplate.questions)
    const baseQuestions = [
      'code',
      'name',
      'subjectCode',
      'courseNumber',
      'longName',
      'college',
      'status',
      'institution',
      'institutionId',
      'rawCourseId',
      'crseOfferNbr',
      'customFields.catalogAttributes',
      'customFields.rawCourseId',
    ]
    const courseQuestionsDataKeys = _uniq(
      courseQuestions
        .map((q) => {
          if (q.custom) return `customFields.${q.dataKey}`
          // When dataKey contains `*` (e.g. 'components.*.finalExamType' which means that field is displaying array of all final exam types)
          // we want to fetch all components
          // we can remove that when backend will support '*' by itself
          else if (q.dataKey.includes('*'))
            return q.dataKey.substring(0, q.dataKey.indexOf('*') - 1)
          // This helps to avoid mongodb $project collision (e.g. 'credits' & 'credits.creditHouts')
          else if (q.dataKey.includes('.'))
            return q.dataKey.substring(0, q.dataKey.indexOf('.') - 1)
          else return q.dataKey
        })
        .concat(baseQuestions)
    )

    const hasDependenciesField = !!Object.values(
      coursePageTemplate.questions
    ).find((field) => field.questionId === 'dependencies')
    const hasCrossListedCoursesField = !!Object.values(
      coursePageTemplate.questions
    ).find(
      (field) =>
        field.questionId === 'crossListedCourses' ||
        field.questionType === 'crossListedCourseSelect'
    )

    const api = new CoursesService($axios, store)
    const { effectiveDatesRange } = query;
    const enableHistoricalRevisions = store.state?.settings?.settings?.enableHistoricalRevisions;
    try {
      const courseParams = {
        columns: courseQuestionsDataKeys,
        formatDependents: hasDependenciesField,
        includeCrosslisted: hasCrossListedCoursesField,
        includeMappedDocumentItems: true,
        ...(enableHistoricalRevisions && effectiveDatesRange ? { effectiveDatesRange } : {}) // to disable historical revisions if turned of for catalog
      };

      const [course, rootRevision, { terms }, { semesterNames }] = await Promise.all([
        api.getCourse(params.id, courseParams),
        api.getCourse(params.id, {
          columns: 'id,courseGroupId,effectiveStartDate,effectiveEndDate',
          includeMappedDocumentItems: false,
        }),
        $axios.$get(`/${store.state.settings.school}/general/terms`),
        $axios.$get(`/${store.state.settings.school}/general/semesterNames`),
      ])

      if (enableHistoricalRevisions) {
        if (!course) {
          redirect('/404')
          return
        }
      } else {
        if (!rootRevision) {
          redirect('/404')
          return
        }
      }

      return {
        course: {
          ...(course.customFields || {}),
          ...course,
          instructionalMethods: expandCustomFieldsIntoRootObject(course.instructionalMethods || []),
        },
        terms,
        semesterNames,
        rootRevision
      }
    } catch (e) {
      console.error(e)
      $bugsnag.notify(e)
      redirect('/404')
    }
  },
  created() {
    this.$store.commit('page/SET_CURRENT_PAGE_LABEL', this.courseDisplayHeading)
  },
  provide() {
    return {
      terms: this.terms,
      semesterNames: this.semesterNames,
      rootRevision: this.rootRevision,
      currentRevision: this.course,
    }
  },
  watchQuery: ['effectiveDatesRange'],
}
</script>

<style lang="css" scoped>
.nuxt-link-active {
  @apply border-l border-t border-r rounded-t;
}
.link {
  cursor: pointer;
}
.link h2 {
  font-size: 16px;
}
</style>
